import React, { useCallback, useContext, useRef, useState } from 'react';
import { AlertCircle, AlertTriangle, ChevronDown, ChevronRight, Edit2, File, Link as LinkIcon, Plus, Save, Trash2, Upload, X } from 'lucide-react';
import { useChatbot } from '../../../contexts/ChatbotContext';
import { useAISettings } from '../../../hooks/useAISettings';
import { supabase } from '../../../lib/supabase';
import { extractTextFromPDF } from '../../../utils/pdfExtractor';
import { useKnowledgeBase } from '../../../hooks/useKnowledgeBase';
import { AISettingsContext } from '../../../pages/settings/AISettings';
import { UrlScraper } from './UrlScraper';
import { motion, AnimatePresence } from 'framer-motion';

interface KnowledgeItem {
  id: string;
  name: string;
  type: 'file' | 'text' | 'url';
  content?: string;
  url?: string;
  metadata?: Record<string, any>;
}

interface EditableContentProps {
  item: KnowledgeItem;
  onSave: (id: string, content: string) => void;
  onDelete: (id: string) => void;
  isLimitExceeded: boolean;
}

function EditableContent({ item, onSave, onDelete, isLimitExceeded }: EditableContentProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [editedContent, setEditedContent] = useState(item.content || '');
  const [isHovered, setIsHovered] = useState(false);
  
  // Get first line or first X characters as title
  const getTitle = (content: string) => {
    const firstLine = content.split('\n')[0].trim();
    return firstLine.length > 50 ? firstLine.substring(0, 50) + '...' : firstLine;
  };

  const [title, setTitle] = useState(() => getTitle(item.content || item.name));

  const handleSave = () => {
    if (isLimitExceeded) return;
    onSave(item.id, editedContent);
    setIsEditing(false);
    setIsExpanded(false);
    setTitle(getTitle(editedContent));
  };

  const getFileIcon = () => {
    if (item.type === 'file' && item.metadata?.fileType === 'application/pdf') {
      return <File className="w-4 h-4 text-red-400" />;
    }
    if (item.type === 'url') {
      return <LinkIcon className="w-4 h-4 text-blue-400" />;
    }
    return <File className="w-4 h-4 text-gray-400" />;
  };

  const handleClick = () => {
    if (!isEditing) {
      setIsExpanded(!isExpanded);
    }
  };

  // Estimer la taille du contenu
  const getContentSize = () => {
    const content = item.content || '';
    if (content.length < 1000) return `${content.length} caractères`;
    return `${(content.length / 1000).toFixed(1)}k caractères`;
  };

  const containerVariants = {
    initial: { opacity: 0, y: -10 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.2 } },
    exit: { opacity: 0, y: 10, transition: { duration: 0.2 } }
  };

  if (isEditing) {
    return (
      <motion.div 
        className="bg-white/5 rounded-lg p-4 space-y-4 border border-white/10"
        initial={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
        animate={{ backgroundColor: 'rgba(79, 70, 229, 0.05)' }}
        transition={{ duration: 0.3 }}
      >
        <textarea
          value={editedContent}
          onChange={(e) => setEditedContent(e.target.value)}
          className={`w-full bg-black/20 border ${isLimitExceeded ? 'border-red-500/50' : 'border-white/10'} rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors`}
          rows={8}
          autoFocus
        />
        <div className="flex justify-between gap-2">
          {isLimitExceeded && (
            <div className="flex items-center gap-2 text-red-400 text-sm">
              <AlertTriangle className="w-4 h-4" />
              <span>Cannot save - Character limit exceeded</span>
            </div>
          )}
          <div className="flex gap-2 ml-auto">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                setIsEditing(false);
                setEditedContent(item.content || '');
              }}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 text-gray-300 hover:bg-white/10 transition-colors"
            >
              <X className="w-4 h-4" />
              Cancel
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleSave}
              disabled={isLimitExceeded}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              Save
            </motion.button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      className={`bg-white/5 rounded-lg p-4 group border border-transparent hover:border-white/10 transition-colors ${isHovered ? 'bg-white/8' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      layout
      variants={containerVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={handleClick}
      >
        <div className="flex items-center gap-2 flex-1">
          <motion.div
            initial={{ rotate: 0 }}
            animate={{ rotate: isExpanded ? 90 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </motion.div>
          {getFileIcon()}
          <h4 className="font-medium text-white">{title}</h4>
          {item.metadata?.fileType && (
            <span className="text-xs text-gray-400">
              ({item.metadata.fileType.split('/')[1].toUpperCase()})
            </span>
          )}
          <span className="text-xs text-gray-500 ml-2">
            {getContentSize()}
          </span>
        </div>
        <div className={`flex items-center gap-2 ${isHovered ? 'opacity-100' : 'opacity-0'} transition-opacity`}>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={(e) => {
              e.stopPropagation();
              setIsEditing(true);
              setIsExpanded(true);
            }}
            className="p-1 text-gray-400 hover:text-white transition-colors"
            disabled={isLimitExceeded}
          >
            <Edit2 className="w-4 h-4" />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.1, color: "#f87171" }}
            whileTap={{ scale: 0.9 }}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(item.id);
            }}
            className="p-1 text-red-400 hover:text-red-300 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </motion.button>
        </div>
      </div>

      <AnimatePresence>
        {isExpanded && !isEditing && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="mt-4 pl-8 pr-2 py-2 bg-black/20 rounded-lg">
              <pre className="text-gray-400 whitespace-pre-wrap text-sm">
                {item.content}
              </pre>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export function KnowledgeBase() {
  const { chatbotPublicId } = useChatbot();
  const { knowledgeBase, addKnowledge, removeKnowledge, updateKnowledge, loading, error } = useAISettings();
  const [urlInput, setUrlInput] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [scrapingError, setScrapingError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { scrapeUrl, loading: scrapeLoading, error: scrapeError } = useKnowledgeBase();
  const { isOverLimit } = useContext(AISettingsContext);
  const [activeTab, setActiveTab] = useState<'url' | 'file' | 'text'>('url');

  const handleFileUpload = useCallback(async (file: File) => {
    if (!chatbotPublicId || isOverLimit) return;

    try {
      setUploadError(null);

      // Validation du type de fichier
      if (!file.type.startsWith('text/') && file.type !== 'application/pdf') {
        throw new Error('Only text and PDF files are supported');
      }

      // Validation de la taille (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('File size must not exceed 10MB');
      }

      // Upload file to storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `knowledge-base/${chatbotPublicId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('chatbot-assets')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('chatbot-assets')
        .getPublicUrl(filePath);

      // Extract content based on file type
      let content = '';
      if (file.type.startsWith('text/')) {
        content = await file.text();
      } else if (file.type === 'application/pdf') {
        content = await extractTextFromPDF(publicUrl);
      }

      // Add to knowledge base
      await addKnowledge({
        type: 'file',
        name: file.name,
        url: publicUrl,
        content,
        metadata: {
          fileType: file.type,
          fileSize: file.size
        }
      }, chatbotPublicId);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      setUploadError(err instanceof Error ? err.message : 'Error uploading file');
    }
  }, [addKnowledge, chatbotPublicId, isOverLimit]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!chatbotPublicId || !e.dataTransfer.files || e.dataTransfer.files.length === 0 || isOverLimit) return;

    const file = e.dataTransfer.files[0];
    await handleFileUpload(file);
  }, [chatbotPublicId, handleFileUpload, isOverLimit]);

  const handleTextSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!chatbotPublicId || isOverLimit) return;
    
    const formData = new FormData(e.currentTarget);
    const text = formData.get('knowledgeText') as string;
    if (text.trim()) {
      await addKnowledge({
        type: 'text',
        name: text.split('\n')[0].trim() || 'Text content',
        content: text
      }, chatbotPublicId);
      (e.target as HTMLFormElement).reset();
    }
  };

  const handleUpdateContent = async (id: string, content: string) => {
    if (!chatbotPublicId || isOverLimit) return;
    
    const item = knowledgeBase.find(item => item.id === id);
    if (!item) return;

    await updateKnowledge({
      ...item,
      content,
      name: content.split('\n')[0].trim() || item.name
    }, chatbotPublicId);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'url':
        return <UrlScraper isLimitExceeded={isOverLimit} />;
      case 'file':
        return (
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`relative border-2 border-dashed rounded-xl p-8 transition-colors cursor-pointer ${
              isOverLimit ? 'border-red-500/50 opacity-50' : 'hover:border-indigo-500/50'
            }`}
          >
            <input
              type="file"
              ref={fileInputRef}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              accept=".pdf,.txt"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file && !isOverLimit) handleFileUpload(file);
              }}
              disabled={isOverLimit}
            />

            <div className="text-center">
              <Upload className={`mx-auto h-12 w-12 ${isOverLimit ? 'text-red-400' : 'text-gray-400'}`} />
              {isOverLimit ? (
                <div className="mt-2 flex flex-col items-center gap-2">
                  <div className="flex items-center gap-2 text-red-400">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="font-medium">Cannot add more documents</span>
                  </div>
                  <p className="text-xs text-red-400/80">
                    Character limit exceeded. Please remove some content before adding more.
                  </p>
                </div>
              ) : (
                <>
                  <p className="mt-2 text-sm text-gray-400">
                    Drag and drop a PDF or text file here, or click to select one.
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    PDF or TXT up to 10MB
                  </p>
                </>
              )}
            </div>

            {uploadError && (
              <div className="mt-4 bg-red-500/10 text-red-400 p-3 rounded-lg text-sm">
                {uploadError}
              </div>
            )}
          </div>
        );
      case 'text':
        return (
          <form onSubmit={handleTextSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Add text
              </label>
              <textarea
                name="knowledgeText"
                rows={4}
                className={`w-full bg-white/5 border ${isOverLimit ? 'border-red-500/50' : 'border-white/10'} rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                placeholder="Enter your text here..."
                disabled={isOverLimit}
              />
            </div>
            <div className="flex items-center justify-between">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading || isOverLimit}
                className="bg-indigo-500/20 text-indigo-400 px-4 py-2 rounded-lg hover:bg-indigo-500/30 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add to the base
              </motion.button>
              
              {isOverLimit && (
                <div className="flex items-center gap-2 text-red-400 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  <span>Cannot add - Character limit exceeded</span>
                </div>
              )}
            </div>
          </form>
        );
      default:
        return null;
    }
  };

  const tabButtonClass = (tab: 'url' | 'file' | 'text') => 
    `px-4 py-2 text-sm font-medium rounded-lg ${
      activeTab === tab 
        ? 'bg-indigo-500/20 text-indigo-400' 
        : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-gray-300'
    } transition-colors`;

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-500/10 text-red-400 p-4 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      <div className="space-y-6">
        <div className="flex space-x-2 mb-6">
          <motion.button
            whileHover={{ y: -2 }}
            whileTap={{ y: 0 }}
            className={tabButtonClass('url')}
            onClick={() => setActiveTab('url')}
          >
            <div className="flex items-center gap-2">
              <LinkIcon className="w-4 h-4" />
              Importer URL
            </div>
          </motion.button>
          <motion.button
            whileHover={{ y: -2 }}
            whileTap={{ y: 0 }}
            className={tabButtonClass('file')}
            onClick={() => setActiveTab('file')}
          >
            <div className="flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Ajouter un fichier
            </div>
          </motion.button>
          <motion.button
            whileHover={{ y: -2 }}
            whileTap={{ y: 0 }}
            className={tabButtonClass('text')}
            onClick={() => setActiveTab('text')}
          >
            <div className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Ajouter du texte
            </div>
          </motion.button>
        </div>

        {renderTabContent()}
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <h4 className="text-sm font-medium text-gray-300">Documents et textes ajoutés</h4>
          {knowledgeBase.length > 0 && (
            <span className="text-xs text-gray-400">
              {knowledgeBase.length} {knowledgeBase.length === 1 ? 'élément' : 'éléments'}
            </span>
          )}
        </div>
        
        <AnimatePresence>
          {knowledgeBase.length === 0 ? (
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }}
              className="bg-white/5 rounded-lg p-6 text-center text-gray-400"
            >
              <div className="flex flex-col items-center gap-2">
                <File className="w-12 h-12 text-gray-500 opacity-50" />
                <p>Aucun document ajouté</p>
                <p className="text-sm text-gray-500">
                  Ajoutez des documents, des URLs ou du texte pour enrichir la base de connaissances de votre chatbot
                </p>
              </div>
            </motion.div>
          ) : (
            <motion.div 
              className="space-y-4"
              layout
            >
              {knowledgeBase.map((item) => (
                <EditableContent
                  key={item.id}
                  item={item}
                  onSave={handleUpdateContent}
                  onDelete={(id) => removeKnowledge(id, chatbotPublicId)}
                  isLimitExceeded={isOverLimit}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}